import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  StaffContractCatalogItemResponse,
  StaffContractHistoryResponse,
  StaffContractStatus,
  StaffContractTerminationRequest,
  StaffEmployeeDocumentRequest,
  StaffEmployeeDocumentResponse,
  StaffEmployeeDocumentStatus,
  StaffEmploymentContractRequest,
  StaffEmploymentContractResponse,
} from "../model/employment.model";
import { BASE_PATH } from "../variables";

@Injectable({ providedIn: "root" })
export class StaffEmploymentService {
  private readonly apiUrl: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(BASE_PATH) basePath: string,
  ) {
    this.apiUrl = `${basePath.replace(/\/$/, "")}/api/v1`;
  }

  listContracts(
    employeeId?: number,
    status?: StaffContractStatus,
  ): Observable<StaffEmploymentContractResponse[]> {
    let params = new HttpParams();
    if (employeeId != null) params = params.set("employeeId", employeeId);
    if (status) params = params.set("status", status);
    return this.http.get<StaffEmploymentContractResponse[]>(
      `${this.apiUrl}/contracts`,
      { params },
    );
  }

  getContract(contractId: number): Observable<StaffEmploymentContractResponse> {
    return this.http.get<StaffEmploymentContractResponse>(
      `${this.apiUrl}/contracts/${contractId}`,
    );
  }

  createContract(
    request: StaffEmploymentContractRequest,
  ): Observable<StaffEmploymentContractResponse> {
    return this.http.post<StaffEmploymentContractResponse>(
      `${this.apiUrl}/contracts`,
      request,
    );
  }

  createContractWithDocument(
    request: StaffEmploymentContractRequest,
    file: File,
  ): Observable<StaffEmploymentContractResponse> {
    return this.http.post<StaffEmploymentContractResponse>(
      `${this.apiUrl}/contracts`,
      this.contractFormData(request, file),
    );
  }

  updateContract(
    contractId: number,
    request: StaffEmploymentContractRequest,
  ): Observable<StaffEmploymentContractResponse> {
    return this.http.put<StaffEmploymentContractResponse>(
      `${this.apiUrl}/contracts/${contractId}`,
      request,
    );
  }

  updateContractWithDocument(
    contractId: number,
    request: StaffEmploymentContractRequest,
    file: File,
  ): Observable<StaffEmploymentContractResponse> {
    return this.http.put<StaffEmploymentContractResponse>(
      `${this.apiUrl}/contracts/${contractId}`,
      this.contractFormData(request, file),
    );
  }

  activateContract(
    contractId: number,
  ): Observable<StaffEmploymentContractResponse> {
    return this.http.post<StaffEmploymentContractResponse>(
      `${this.apiUrl}/contracts/${contractId}/activate`,
      {},
    );
  }

  terminateContract(
    contractId: number,
    request: StaffContractTerminationRequest,
  ): Observable<StaffEmploymentContractResponse> {
    return this.http.post<StaffEmploymentContractResponse>(
      `${this.apiUrl}/contracts/${contractId}/terminate`,
      request,
    );
  }

  cancelContract(
    contractId: number,
  ): Observable<StaffEmploymentContractResponse> {
    return this.http.post<StaffEmploymentContractResponse>(
      `${this.apiUrl}/contracts/${contractId}/cancel`,
      {},
    );
  }

  listContractHistory(
    contractId: number,
  ): Observable<StaffContractHistoryResponse[]> {
    return this.http.get<StaffContractHistoryResponse[]>(
      `${this.apiUrl}/contracts/${contractId}/history`,
    );
  }

  listContractTypes(): Observable<StaffContractCatalogItemResponse[]> {
    return this.listCatalog("contract-types");
  }

  listContinuityTypes(): Observable<StaffContractCatalogItemResponse[]> {
    return this.listCatalog("continuity-types");
  }

  listRenewalLimits(): Observable<StaffContractCatalogItemResponse[]> {
    return this.listCatalog("renewal-limits");
  }

  listWorkingModes(): Observable<StaffContractCatalogItemResponse[]> {
    return this.listCatalog("working-modes");
  }

  listEmployeeDocuments(
    employeeId?: number,
    status?: StaffEmployeeDocumentStatus,
  ): Observable<StaffEmployeeDocumentResponse[]> {
    let params = new HttpParams();
    if (employeeId != null) params = params.set("employeeId", employeeId);
    if (status) params = params.set("status", status);
    return this.http.get<StaffEmployeeDocumentResponse[]>(
      `${this.apiUrl}/employee-documents`,
      { params },
    );
  }

  registerEmployeeDocument(
    request: StaffEmployeeDocumentRequest,
  ): Observable<StaffEmployeeDocumentResponse> {
    return this.http.post<StaffEmployeeDocumentResponse>(
      `${this.apiUrl}/employee-documents`,
      request,
    );
  }

  archiveEmployeeDocument(
    documentId: number,
  ): Observable<StaffEmployeeDocumentResponse> {
    return this.http.post<StaffEmployeeDocumentResponse>(
      `${this.apiUrl}/employee-documents/${documentId}/archive`,
      {},
    );
  }

  private listCatalog(
    resource: string,
  ): Observable<StaffContractCatalogItemResponse[]> {
    return this.http.get<StaffContractCatalogItemResponse[]>(
      `${this.apiUrl}/${resource}`,
    );
  }

  private contractFormData(
    request: StaffEmploymentContractRequest,
    file: File,
  ): FormData {
    const form = new FormData();
    form.append(
      "contract",
      new Blob([JSON.stringify(request)], { type: "application/json" }),
    );
    form.append("file", file);
    return form;
  }
}
